import { createSlice } from "@reduxjs/toolkit";
import { isExpired } from "react-jwt";
import { v4 as uuidv4 } from 'uuid';

import Cookies from "js-cookie";
import { setUserId } from "../../services/appsflyer";

const initialState = {
  data: [],
  user: {} as IUser,
  status: "idle", //'idle' | 'loading' | 'succeeded' | 'failed'
  error: "",
  hasPropaganda: false,
  refresh: false,
  authenticated: false,
  familyFriendly: localStorage.getItem("familyFriendly")
    ? localStorage.getItem("familyFriendly") === "true"
    : true,
  CUID: localStorage.getItem('CUID') ?? uuidv4(),
};

export const UserSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    logout: (state, action) => {
      Cookies.set("authenticated", JSON.stringify(false), {
        secure: false,
        sameSite: "lax",
        path: "/",
        domain: process.env.REACT_APP_COOKIE_DOMAIN,
        expires: 30,
      });
      Cookies.set("token", "", {
        secure: false,
        sameSite: "lax",
        path: "/",
        domain: process.env.REACT_APP_COOKIE_DOMAIN,
        expires: 30,
      });
      Cookies.set("user", JSON.stringify({}), {
        secure: false,
        sameSite: "lax",
        path: "/",
        domain: process.env.REACT_APP_COOKIE_DOMAIN,
        expires: 30,
      });
      const cookies = Cookies.get();
      for (const cookieName in cookies) {
        if (cookieName !== "language") {
          Cookies.remove(cookieName, { path: "/" });
        }
      }
      state.user = {} as IUser;
      state.authenticated = false;
      state.hasPropaganda = false;
      const id = uuidv4();
      state.CUID = id;
      localStorage.setItem("CUID", id);
      setUserId(id);
    },
    initUser: (state, action) => {
      const token = Cookies.get("token");
      const authenticated = JSON.parse(Cookies.get("authenticated") || "null");
      const user = JSON.parse(Cookies.get("user") || "null") as IUser;
      if (token && !isExpired(token) && user?.cuid) {
        state.authenticated = authenticated;
        state.user = user;
        state.CUID = user?.cuid;
        localStorage.setItem("CUID", user?.cuid);
        setUserId(user?.cuid);
        if (user?.game && user.game.find((g: IGame) => g.name === "propaganda")) {
          state.hasPropaganda = true;
        }
      } else {
        Cookies.set("authenticated", JSON.stringify(false), {
          secure: false,
          sameSite: "lax",
          path: "/",
          domain: process.env.REACT_APP_COOKIE_DOMAIN,
          expires: 30,
        });
        Cookies.set("token", "", {
          secure: false,
          sameSite: "lax",
          path: "/",
          domain: process.env.REACT_APP_COOKIE_DOMAIN,
          expires: 30,
        });
        Cookies.set("user", JSON.stringify({}), {
          secure: false,
          sameSite: "lax",
          path: "/",
          domain: process.env.REACT_APP_COOKIE_DOMAIN,
          expires: 30,
        });
        const cookies = Cookies.get();
        for (const cookieName in cookies) {
          if (cookieName !== "language")
            Cookies.remove(cookieName, {
              path: "/",
              domain: process.env.REACT_APP_COOKIE_DOMAIN,
            });
        }
        state.user = {} as IUser;
        state.authenticated = false;
        if (token && isExpired(token)) {
          const id = uuidv4();
          state.CUID = id;
          localStorage.setItem("CUID", id)
          setUserId(id);
        }
        else
          setUserId(localStorage.getItem("CUID") || uuidv4());
      }
    },
    setUser: (state, action) => {
      state.user = action.payload;
      if (action.payload?.game?.find((g: IGame) => g.name === "propaganda")) {
        state.hasPropaganda = true;
      } else {
        state.hasPropaganda = false;
      }
    },
    setAuthenticated: (state, action) => {
      state.authenticated = true;
      const user = action.payload.user;
      state.CUID = action.payload.user.cuid;
      localStorage.setItem("CUID", action.payload.user.cuid);
      setUserId(action.payload.user.cuid);
      state.user = user;
      if (user.game.find((g: IGame) => g.name === "propaganda")) {
        state.hasPropaganda = true;
      }
      Cookies.set("authenticated", JSON.stringify(true), {
        secure: false,
        sameSite: "lax",
        path: "/",
        domain: process.env.REACT_APP_COOKIE_DOMAIN,
        expires: 30,
      });
      Cookies.set("token", action.payload.token, {
        secure: false,
        sameSite: "lax",
        path: "/",
        domain: process.env.REACT_APP_COOKIE_DOMAIN,
        expires: 30,
      });
      Cookies.set("user", JSON.stringify(action.payload.user), {
        secure: false,
        sameSite: "lax",
        path: "/",
        domain: process.env.REACT_APP_COOKIE_DOMAIN,
        expires: 30,
      });
      state.refresh = true;
    },
    setHasPropaganda: (state, action) => {
      state.hasPropaganda = action.payload;
    },
    setSyncHasProganda: (state, action) => {
      state.hasPropaganda = true;
      state.user.game = action.payload.game
    },
    setRefresh: (state, action) => {
      state.refresh = false;
    },
    setFamilyFriendly: (state, action) => {
      state.familyFriendly = action.payload;
      localStorage.setItem("familyFriendly", action.payload);
    },
  },
});

export interface IUser {
  name?: string;
  email?: string;
  familyName?: boolean;
  phone?: string;
  game?: Array<IGame>;
  cuid?: string;
  country?:string;
  city?:string;
  postcode?:string;
  state?:string;
  street?:string;
}

export interface IGame {
  name?: string;
  description?: string;
  price?: boolean;
}

// Action creators are generated for each case reducer function
export const {
  setAuthenticated,
  logout,
  initUser,
  setHasPropaganda,
  setRefresh,
  setFamilyFriendly,
  setUser,
  setSyncHasProganda,
} = UserSlice.actions;

export default UserSlice.reducer;
